<template>
    <div class="register">
        <div class="fd-list-header">
            <h1 class="fd-list-header__brand">Redhill Food Distribution</h1>
        </div>

        <div class="card">
            <div class="card-content">
                <h1 class="title is-6 has-text-centered">Volunteer Registration</h1>
                <div v-if="!emailSent" class="block">
                    <div class="field">
                        <p class="control">
                            <input v-model="name" class="input" type="text" placeholder="Full Name">
                        </p>
                    </div>
                    <div class="field">
                        <p class="control">
                            <input v-model="phone" class="input" type="tel" inputmode="tel" placeholder="Phone Number">
                        </p>
                    </div>
                    <div class="field">
                        <p class="control">
                            <input v-model="email" class="input" type="email" inputmode="email" placeholder="Email">
                        </p>
                    </div>
                    <div class="field">
                        <p class="control">
                            <input v-model="password" class="input" type="password" placeholder="Password (min 6 characters)">
                        </p>
                    </div>

                    <div v-if="validationErrors.length" class="block notification is-danger is-light">
                        <p>
                            <b>Please correct the following error(s):</b>
                        <ul>
                            <li v-for="validationError in validationErrors" :key="validationError">{{ validationError }}</li>
                        </ul>
                        </p>
                    </div>

                    <base-spinner v-if="isLoading"></base-spinner>
                    <div v-else class="field">
                        <p class="control">
                            <button :disabled="!name || !phone || !email || !password"
                                class="button is-primary is-fullwidth is-medium" :class="[isLoading ? 'is-loading' : '']"
                                @click="registerUser">
                                Register
                            </button>
                        </p>
                    </div>
                    <div class="field">
                        <p class="control">
                            <button class="button is-white is-fullwidth is-medium" style="border: 1px solid #ddd"
                                :class="[isGoogleLoading ? 'is-loading' : '']" @click="registerWithGoogle">
                                <span class="icon"><i class="fab fa-google"></i></span>
                                <span>Continue with Google</span>
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="block">
            <div v-if="userExists" class="notification is-warning">
                <p>An account already exists with this email.</p>
                <br />
                <router-link to="/login">
                    <button class="button is-info is-rounded is-medium">
                        Login
                    </button>
                </router-link>
            </div>
            <div v-else-if="emailSent" class="notification is-info is-light">
                <p><strong>Check your inbox or <span style="color: #ff3860;">spam</span>!</strong></p>
                <p>We sent a verification link to <strong>{{ email }}</strong>. Click it to verify your account.</p>
                <br />
                <button class="button is-info is-rounded is-medium mr-2"
                    :class="{ 'is-loading': isResending }"
                    :disabled="isResending"
                    @click="resendEmail">
                    Resend email
                </button>
                <router-link to="/login" class="mt-3" style="display: block;">
                    <button class="button is-dark is-rounded is-medium">
                        Back to login
                    </button>
                </router-link>
                <p v-if="resendMessage" class="mt-2">{{ resendMessage }}</p>
            </div>
        </div>

        <div v-if="!emailSent && !userExists" class="block notification">
            <h1 class="title is-6">I already have an account.</h1>
            <router-link to="/login">
                <button class="button is-primary is-rounded is-medium">Login</button>
            </router-link>
        </div>
    </div>
</template>

<script>
import { auth } from '@/firebase/firebaseInit';
import { createUserWithEmailAndPassword, sendEmailVerification } from '@firebase/auth';

export default {
    data() {
        return {
            isLoading: false,
            isGoogleLoading: false,
            isResending: false,
            isFormValid: true,
            validationErrors: [],
            name: "",
            phone: "",
            email: "",
            password: "",
            userExists: false,
            emailSent: false,
            resendMessage: null
        }
    },
    watch: {
        email() {
            this.email = this.email.toLowerCase()
        }
    },
    methods: {
        async registerUser() {
            this.isLoading = true;
            this.userExists = false;
            this.emailSent = false;
            this.validateForm();
            if (this.isFormValid) {
                try {
                    const { user } = await createUserWithEmailAndPassword(auth, this.email, this.password);
                    await this.$store.dispatch('user/createUser', {
                        name: this.name.trim(),
                        email: this.email,
                        phone: this.phone.trim()
                    });
                    await sendEmailVerification(user);
                    this.emailSent = true;
                } catch (error) {
                    console.log(error.message);
                    this.userExists = true;
                    this.password = "";
                }
            }
            this.isLoading = false;
        },
        async resendEmail() {
            this.isResending = true;
            this.resendMessage = null;
            try {
                await sendEmailVerification(auth.currentUser);
                this.resendMessage = 'Verification email sent!';
            } catch (error) {
                this.resendMessage = error.message || 'Failed to resend email.';
            }
            this.isResending = false;
        },
        async registerWithGoogle() {
            this.isGoogleLoading = true;
            try {
                await this.$store.dispatch('user/loginWithGoogle');
            } catch (error) {
                this.validationErrors = [error.message || 'Google sign-in failed.'];
            }
            this.isGoogleLoading = false;
        },
        validateForm() {
            this.isFormValid = false;
            this.validationErrors = [];
            if (!this.name.trim()) {
                this.validationErrors.push("Full name required.");
            }
            if (!this.phone.trim()) {
                this.validationErrors.push("Phone number required.");
            }
            if (!this.email) {
                this.validationErrors.push("Email required.");
            } else {
                var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                if (!re.test(this.email)) {
                    this.validationErrors.push("Email is not valid.");
                }
            }
            if (!this.password) {
                this.validationErrors.push("Password required.");
            } else {
                if (this.password.length < 6) {
                    this.validationErrors.push("Password must be at least 6 chars.");
                }
            }
            if (!this.validationErrors.length) {
                this.isFormValid = true;
            }
        }
    }
}
</script>

<style scoped>
.register {
    text-align: center;
    max-width: 400px;
    padding: 20px;
    margin: 0 auto;
}

.fd-list-header {
    text-align: center;
    padding: 1.25rem 0 0.5rem;
}

.fd-list-header__brand {
    font-size: 1.6rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
}

.card {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    border-radius: 8px;
    border: none;
}
</style>
